import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useTheresiasEventBridge } from "./useTheresiasEventBridge";

const THRESHOLDS = [25, 50, 75, 100] as const;
type Threshold = (typeof THRESHOLDS)[number];

// Below this, a scroll target is too small to be a "page view" — likely a
// dropdown list, autocomplete results, or some other widget-internal scroll.
// Page-style scrollers in this app are always taller than this.
const MIN_SCROLLER_HEIGHT_PX = 200;

// Sub-pixel tolerance for "at the bottom". Chrome / Safari momentum scroll
// and fractional DPR commonly leave scrollTop 0.3–1.5 px short of the true
// bottom on a continuously-paginated list (Shop Travel flight / stay
// results). Without this carve-out, percent stalls at ~99.7 % on the final
// page and the 100 % bucket never fires.
const AT_END_TOLERANCE_PX = 1.5;

// On contentHeight growth ≥ this many pixels, treat the scroller as having
// received "new content" (pagination, lazy-load, etc.) and re-arm any
// threshold the user has NOT yet crossed on the new height. 400 px = bigger
// than a single image-decode layout shift (~150 px typical) but smaller than
// any pagination batch (~10 items × 80–250 px = thousands of px). Keeps
// false-positive resets from individual image loads off the table while
// reliably catching real pagination.
const GROWTH_RESET_PX = 400;

/**
 * Scroll-depth telemetry. Mount once at App root.
 *
 * Fires 4 Theresias events at 25 / 50 / 75 / 100 percent thresholds, once
 * per route per scroll container:
 *   browser-native-scroll-depth-25
 *   browser-native-scroll-depth-50
 *   browser-native-scroll-depth-75
 *   browser-native-scroll-depth-100
 *
 * Implementation:
 *   - ONE capture-phase scroll listener on document. Scroll events do NOT
 *     bubble, but the capture phase still fires from document down to the
 *     target — so this single listener catches scroll on the page AND on
 *     every inner `overflow: auto` container (BookingInProgress's
 *     `.scrollContainer`, ItineraryDetails, desktop-chat sidebar, History,
 *     Lares, the Shop Travel flight/stay listings, etc.).
 *   - Metrics are read from event.target so we measure the ACTUAL element
 *     being scrolled, not the page-level body/html. The legacy
 *     window-scroller path (target = document / html / body) is preserved
 *     and reads body.scrollTop with the iOS Safari body-scroller gotcha
 *     fallback per CLAUDE.md §"Scroll container gotcha".
 *   - rAF-throttled; per-scroller dedupe via WeakMap<scrollerKey,
 *     {lastHeight, fired: Set<Threshold>}>. Reset on pathname change so
 *     a fresh scroll on a new page re-fires the buckets.
 *   - Pagination semantic: when a scroller's contentHeight grows by ≥
 *     GROWTH_RESET_PX (search-results pagination, lazy-load), the fired
 *     set is re-armed to only the thresholds the user currently exceeds
 *     on the NEW height. So a Shop Travel results list firing 25/50/75/100
 *     on page-1 will, after page-2 loads and the user keeps scrolling,
 *     re-fire 75 and 100 on the new larger content. lastHeight is tracked
 *     as a high-water mark so filter-shrink-then-grow doesn't double-fire.
 *
 * Notes:
 *   - The bucket is encoded in the event name (proposal column M), so the
 *     payload is intentionally minimal — `scroller` (diagnostic string for
 *     which container was scrolled) and `route` are all that ride along.
 *   - chatId / ownerEmail are auto-injected by the bridge when present.
 *   - When a scrollable region is shorter than the viewport (or
 *     scrollable === 0), no events fire — there's nothing to scroll. The
 *     MIN_SCROLLER_HEIGHT_PX floor additionally filters out tiny widget
 *     scrollers (dropdowns, autocomplete lists).
 *   - Per-route dedupe means the FIRST container to cross 25 % on a route
 *     fires that bucket; a second container's later 25 % is silently
 *     deduped. This is the intended semantic — depth measures "how far the
 *     user engaged on this page", and one signal per threshold is enough.
 */
export function useScrollDepthTracking(): void {
  const fireTheresiasEvent = useTheresiasEventBridge();
  const fireRef = useRef(fireTheresiasEvent);
  fireRef.current = fireTheresiasEvent;

  // Per-scroller dedupe state. Each scroller (page-level "document" or an
  // inner overflow:auto Element) gets its own fired-set + high-water-mark
  // content height so growth (pagination, lazy-load) re-arms thresholds the
  // user has not yet crossed on the new content. WeakMap auto-GCs entries
  // when their Element keys are removed from the DOM — no manual cleanup
  // needed for unmounted scrollers.
  type ScrollerState = { lastHeight: number; fired: Set<Threshold> };
  const scrollerStateRef = useRef<WeakMap<object, ScrollerState>>(new WeakMap());
  const rafRef = useRef<number | null>(null);
  // Keep a ref to the latest scroll target — rAF coalesces multiple scroll
  // events into one read per frame, and we want to read the LATEST target's
  // metrics, not whichever target happened to win the race.
  const pendingTargetRef = useRef<EventTarget | null>(null);

  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Reset every scroller's dedupe on route change so the new page can
  // re-fire thresholds. WeakMap has no .clear(); swap to a fresh map.
  useEffect(() => {
    scrollerStateRef.current = new WeakMap();
  }, [pathname]);

  useEffect(() => {
    const onScroll = (event: Event) => {
      // Always remember the freshest target — even if we drop this tick due
      // to the rAF guard, the next-frame read will see the most recent
      // scroller.
      pendingTargetRef.current = event.target;

      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const target = pendingTargetRef.current;
        pendingTargetRef.current = null;

        let scrollY = 0;
        let scrollable = 0;
        let viewport = 0;
        let contentHeight = 0;
        let scrollerName = "";

        const html = document.documentElement;
        const body = document.body;

        if (target === document || target === html || target === body || target === window) {
          // Page-level scroll — read page metrics with the body-scroller
          // fallback (CLAUDE.md §"Scroll container gotcha").
          scrollY = Math.max(
            body?.scrollTop ?? 0,
            html?.scrollTop ?? 0,
            typeof window.scrollY === "number" ? window.scrollY : 0
          );
          contentHeight = Math.max(body?.scrollHeight ?? 0, html?.scrollHeight ?? 0);
          viewport = window.innerHeight || html?.clientHeight || 0;
          scrollable = contentHeight - viewport;
          scrollerName = (body?.scrollTop ?? 0) > 0 ? "BODY" : "WINDOW";
        } else if (target instanceof Element) {
          // Inner-container scroll — common in this app's main routes
          // (BookingInProgress listings, ItineraryDetails, desktop chat).
          // Skip text-entry widgets so typing-induced overflow scrolls
          // don't pollute engagement metrics.
          const tag = target.tagName;
          if (tag === "TEXTAREA" || tag === "INPUT" || tag === "SELECT") return;

          scrollY = target.scrollTop;
          viewport = target.clientHeight;
          contentHeight = target.scrollHeight;
          scrollable = contentHeight - viewport;

          // Below MIN_SCROLLER_HEIGHT_PX the element is a widget-internal
          // scroller (dropdown, autocomplete) rather than a page-content
          // surface; skip.
          if (viewport < MIN_SCROLLER_HEIGHT_PX) return;

          // Diagnostic identifier: tag + first stable class. CSS-module
          // hashed names are fine — same build → same name across users.
          const classStr = (target as HTMLElement).className;
          const firstClass =
            typeof classStr === "string" && classStr
              ? classStr.split(/\s+/).filter(Boolean)[0] || ""
              : "";
          scrollerName = firstClass ? `${tag.toLowerCase()}.${firstClass}` : tag.toLowerCase();
        } else {
          return;
        }

        if (scrollable <= 0) return;
        // Snap to exactly 100 % when the user is within AT_END_TOLERANCE_PX
        // of the true bottom. Required for the 100-bucket to fire on
        // paginated lists (Shop Travel flight / stay results) where Chrome's
        // momentum scrolling leaves scrollTop fractionally short of
        // scrollHeight − clientHeight on the final page. Also clamped to
        // 100 so any other rounding overshoot can't produce > 100 %.
        const atEnd = scrollY + viewport >= contentHeight - AT_END_TOLERANCE_PX;
        const rawPercent = (scrollY / scrollable) * 100;
        const percent = atEnd ? 100 : Math.min(100, rawPercent);
        const route = pathnameRef.current;

        // Resolve scroller key for per-scroller dedupe. Page-level scrolls
        // (target = document / html / body / window) all collapse to the
        // single `document` key so the page is treated as one logical
        // scroller. Inner Elements are their own keys.
        const scrollerKey: object =
          target === document || target === html || target === body || target === window
            ? document
            : (target as Element);

        let state = scrollerStateRef.current.get(scrollerKey);
        if (!state) {
          state = { lastHeight: contentHeight, fired: new Set<Threshold>() };
          scrollerStateRef.current.set(scrollerKey, state);
        } else if (contentHeight > state.lastHeight + GROWTH_RESET_PX) {
          // Pagination / lazy-load appended new content. Pre-fill the fired
          // set with thresholds the user CURRENTLY exceeds on the new
          // larger content (so we don't re-fire those — user already
          // engaged that depth) but DROP forward thresholds so the user can
          // re-fire 75/100 etc. as they scroll through the new content.
          // Use the new contentHeight as the high-water mark for future
          // growth comparisons.
          state.lastHeight = contentHeight;
          const stillFired = new Set<Threshold>();
          for (const t of THRESHOLDS) {
            if (percent >= t) stillFired.add(t);
          }
          state.fired = stillFired;
        }
        // Shrinks (e.g. filter applied) and sub-threshold growth (image
        // decode layout shift) are intentionally ignored — lastHeight stays
        // at the high-water mark so a filter-shrink-then-grow back to the
        // ORIGINAL size doesn't trigger a spurious re-arm of thresholds
        // the user already engaged with.

        // Spec §F: scrollTopPx + contentHPx are the raw measurements the
        // backend uses to verify the threshold computation client-side.
        // `scroller` is the diagnostic — BODY / WINDOW for page-level
        // scrolls, otherwise "CONTAINER" for an inner overflow:auto element.
        const scrollerForSpec: "BODY" | "WINDOW" | "CONTAINER" =
          scrollerName === "BODY" || scrollerName === "WINDOW"
            ? scrollerName
            : "CONTAINER";
        for (const t of THRESHOLDS) {
          if (percent >= t && !state.fired.has(t)) {
            state.fired.add(t);
            fireRef.current(`browser-native-scroll-depth-${t}`, {
              scrollTopPx: Math.round(scrollY),
              contentHPx: Math.round(contentHeight),
              scroller: scrollerForSpec,
              // route + raw scrollerName retained for diagnostics; spec only
              // requires the 3 fields above.
              route,
              scrollerName,
            });
          }
        }
      });
    };

    // Capture-phase listener at document is the universal catch — fires for
    // page-level scrolls (target = document/html/body) AND inner-container
    // scrolls (target = any descendant Element). Scroll events don't bubble,
    // so the capture phase is the only way to observe inner-container
    // scrolls from a single root listener.
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });

    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingTargetRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
